<!--
  (c) Copyright 2024 Calabrio, Inc.
  All Rights Reserved. www.calabrio.com LICENSED MATERIALS
  Property of Calabrio, Inc., Minnesota, USA

  No part of this publication may be reproduced, stored or transmitted,
  in any form or by any means (electronic, mechanical, photocopying,
  recording or otherwise) without prior written permission from Calabrio, Inc.
-->

<template>
  <div :class="{ 'wy-flow-messages': true, 'hasData': hasData }">
    <div class="topic-group-section">
      <div class="div-group-viewer-header div-group-name">
        Customer messages
      </div>
      <div class="div-group-viewer-content" v-loading="messagesLoading">
        <div class="div-group-topics">
          <div v-if="messages && messages.length > 0" class="div-topic-content">
            <div class="div-item" :title="item.messageText" v-for="(item, index) in messages" :key="index"
              v-bind:class="{ selected: (selectedMessage && selectedMessage.messageId == item.messageId) }"
              @click="getTranscriptById(item, $event)">
              <div class="div-ellipsis-item">{{ item.messageText }}</div>
            </div>
          </div>
          <div v-else-if="messages && messages.length == 0" class="div-no-content">
            No messages
          </div>
        </div>
      </div>
      <div class="div-group-viewer-footer div-group-name">
        <div class="div-message-transcripts-viewer-footer">
          <div class="div-transcript-page-infor">Showing {{ pageItemStart }}-{{ pageItemEnd }}</div>
          <div class="div-transcript-pagination">
            <el-pagination :page-size="pageSize" :page-count="pageCount" :current-page="pageNo"
              :disabled="messages && messages.length == 0" prev-text="< Previous" next-text="Next >"
              @current-change="pageChanged" layout="prev, next">
            </el-pagination>
          </div>
        </div>
      </div>
    </div>
    <div class="message-transcripts-section">
      <div class="div-message-transcripts-viewer-header">

        <span class="header-content">
          <el-tooltip v-if="selectedMessage" class="item" effect="dark" placement="bottom">
            <div v-if="selectedMessage && selectedMessage.sentiment == 'pos'" slot="content">Positive sentiment</div>
            <div v-else-if="selectedMessage && selectedMessage.sentiment == 'neg'" slot="content">Negative sentiment</div>
            <div v-else slot="content">Neutral sentiment</div>

            <div class="sentiment-icon-positive" v-if="selectedMessage && selectedMessage.sentiment == 'pos'"></div>
            <div class="sentiment-icon-neutral" v-else-if="selectedMessage && selectedMessage.sentiment == 'neu'"></div>
            <div class="sentiment-icon-negative" v-else-if="selectedMessage && selectedMessage.sentiment == 'neg'"></div>
          </el-tooltip>
          Transcript
        </span>

        <div style="display: flex; flex-direction: column; gap: 2px; margin-top: -8px;">
          <div v-if="selectedMessage" class="date">
            {{ formatMessageDate(selectedMessage.messages[0].datetime) }}
          </div>

          <div v-if="selectedMessage" class="time-range">
            {{ formatHeaderTimeRange(selectedMessage) }}
          </div>
        </div>

        <div style="display: flex;">
          <!-- save transcript -->
          <div v-if="selectedMessage">
            <el-tooltip v-if="selectedMessage.isSavedTranscript" ref="unSaveTooltip" content="unsave transcript"
              effect="dark" placement="bottom">
              <div>
                <el-button v-if="selectedMessage.isSavedTranscript" slot="reference" class="saved-transcript-golden-star"
                  @click="removeSavedTranscript($event, selectedMessage)"></el-button>
              </div>
            </el-tooltip>
            <el-tooltip v-if="!selectedMessage.isSavedTranscript" ref="saveTooltip" content="save transcript"
              effect="dark" placement="bottom">
              <div>
                <el-button v-if="!selectedMessage.isSavedTranscript" slot="reference" class="saved-transcript-grey-star"
                  @click="addSavedTranscript($event, selectedMessage)"></el-button>
              </div>
            </el-tooltip>
          </div>

          <!-- summary -->


          <!-- Show summary -->
          <el-tooltip v-show="isTranscriptSummaryEnabled && !showTranscriptSummary && selectedMessage"
            ref="summaryTooltip" :content="'View summary'" effect="dark" placement="bottom">
            <el-button class="summary-icon" @click="handleShowSummary(selectedMessage)"></el-button>
          </el-tooltip>
          <el-tooltip v-show="isTranscriptSummaryEnabled && showTranscriptSummary && selectedMessage" ref="summaryTooltip"
            :content="'Hide summary'" effect="dark" placement="bottom">
            <el-button class="summary-icon" @click="handleHideSummary(selectedMessage)"></el-button>
          </el-tooltip>

          <div class="info-mark">
            <el-popover v-if="selectedMessage" placement="top" title="" width="280"
              popper-class="contact-reason-group-topics-tab-info-pop-over" trigger="hover">
              <div>
                <div class="transcripts-viewer-header-right">
                  <div class="popper-item"><b>Conversation ID</b><br /> {{ selectedMessage.conversationId }}</div>
                  <div class="popper-item" v-if="selectedMessage.agentId !== '--'"><b>Agent ID</b><br />
                    {{ selectedMessage.agentId }}</div>
                  <div class="popper-item" v-if="selectedMessage.conversationType !== '--'"><b>Handling type</b><br />
                    <span v-if="selectedMessage.conversationType == 'BotOnlyContained'"> VA contained</span>
                    <span v-if="selectedMessage.conversationType == 'BotToLC'">VA engaged with live agent connected</span>
                    <span v-if="selectedMessage.conversationType == 'LCOnly'">Live Agent Only</span>
                    <span v-if="selectedMessage.conversationType == 'BotOnlyEscalated'">Live agent requested but not connected</span>
                  </div>
                  <div class="popper-item" v-if="selectedMessage.custType !== '--'"><b>Channel</b><br />
                    {{ convertTranscriptCustType(selectedMessage.custType) }}</div>
                  <div class="popper-item" v-if="selectedMessage.agentQueue !== '--'"><b>Agent queue</b><br />
                    {{ selectedMessage.agentQueue }}</div>
                  <div class="popper-item" v-if="selectedMessage.agentGroup !== '--'"><b>Agent group</b><br />
                    {{ selectedMessage.agentGroup }}</div>
                  <div class="popper-item" v-if="selectedMessage.handleTime !== '--'"><b>Handling time</b><br />
                    {{ handleTimeInNiceFormat(selectedMessage.handleTime) }}</div>
                </div>
              </div>
              <el-button slot="reference" class="el-infor-icon"></el-button>
            </el-popover>
          </div>
        </div>
      </div>
      <div class="div-message-transcripts-viewer-content" v-loading="transcriptLoading">
        <div v-if="messagesTranscripts && messagesTranscripts.length > 0">
          <div class="transcriptSummary" v-if="showTranscriptSummary && !transcriptSummaryError"
            v-loading="transcriptSummaryLoading">
            <div class="title">
              <div class="summaryTitle"></div>
              Transcript Summary
            </div>
            <div v-if="selectedMessage.contentSummary" class="summary" v-html="contentSummary">
            </div>
            <div class="footer">[ Generated by AI ]</div>
          </div>
          <div v-else-if="transcriptSummaryError" class="transcriptSummaryError">
            <div class="content">
              <div class="info">
                <el-button class="errorIcon" @click="handleHideSummary"></el-button>
                <div class="body">
                  <div>Summary failed to load.</div>
                  <el-button class="tryAgainBtnFlow" @click="handleShowSummary(selectedMessage)">Try again</el-button>
                </div>
              </div>
              <el-button class="closeIcon" @click="handleHideSummary"></el-button>
            </div>
          </div>
          <div v-for=" (transcript, index) in messagesTranscripts" :key="index"
            style="display: flex; flex-direction: column;" class="div-message-transcript-name"
            :class="{ 'div-message-transcript-name-highlighted': transcript.text == selectedMessage.messageText, 'cus-style': transcript.sentBy == 'Customer', 'bot-style': transcript.sentBy == 'Bot', 'agent-style': transcript.sentBy == 'Agent' }">
            <div class="div-sent-by">{{ toTranscriptLabel(transcript.sentBy) }}, <span class="time-stamp-style">{{
              handleTimeFormat(transcript.datetime) }}</span></div>
            &nbsp;
            <div class="div-transcript-text">{{ transcript.text }}</div>
          </div>
        </div>
        <div v-else-if="messagesTranscripts && messagesTranscripts.length == 0" class="div-no-content">
          No transcript
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import moment from 'moment';
import { mapGetters, mapActions } from 'vuex';
import WyUtils from '../../library/wy-utils.js';

export default Vue.extend({
  name: 'wy-flow-messages',

  data() {
    return {
      messagesLoading: false,
      transcriptLoading: false,
      hasData: false,
      searchPath: null,
      messages: null,
      selectedMessage: null,
      messagesTranscripts: null,
      showTranscriptSummary: false,
      transcriptSummaryLoading: false,
      transcriptSummaryError: false,

      pageNo: 1,
      pageSize: 10,
      pageCount: 1,
      pageItemStart: 0,
      pageItemEnd: 0
    }
  },

  computed: {
    ...mapGetters(['config', 'companyConfig', 'dataModel', 'isRoleAllow', 'isC1Auth']),
    isTranscriptSummaryEnabled() {
      if(this.isC1Auth) {
        return this.isRoleAllow(900007);
      }
      else {
        return (this.companyConfig ? this.companyConfig.enableTranscriptSummary : false);
      }
    },
    contentSummary() {
      return this.selectedMessage.contentSummary;
    }
  },

  watch: {
    selectedMessage: {
      handler: function (val) {
        if (val) {
          this.showTranscriptSummary = false;
          this.transcriptSummaryError = false;
        }
      },
      deep: true
    }
  },

  methods: {
    ...mapActions(['fetchFlowsMessages', 'fetchFlowsTopicConversations', 'addTranscript', 'removeTranscript', 'fetchTranscriptSummary']),

    convertTranscriptCustType(custType) {
      const custTypesMap = (this.companyConfig && this.companyConfig.app && this.companyConfig.app.configs && this.companyConfig.app.configs.channelsMapping) || {};
      if (!custTypesMap[custType]) {
        return custType;
      }

      return custTypesMap[custType];
    },

    toTranscriptLabel(val) {
      return WyUtils.toTranscriptLabel(val);
    },

    formatMessageDate(date) {
      return WyUtils.formatDateWithDay(date);
    },

    formatHeaderTimeRange(transcript) {
      return WyUtils.getTimeRange(transcript);
    },
    handleTimeFormat(datetime) {
      return moment(datetime).format("h:mm a")
    },
    getTimeRange(transcripts) {
      if (transcripts && transcripts.length > 1) {
        let startTime = moment(transcripts[0].datetime).format("hh:mm a");
        let endTime = moment(transcripts[transcripts.length - 1].datetime).format("hh:mm a");
        return startTime + ' - ' + endTime;
      }
      else if (transcripts && transcripts.length == 1) {
        return moment(transcripts[0].datetime).format("hh:mm a");
      }
      return '';
    },

    handleTimeInNiceFormat(Millis) {
      let seconds = Millis / 1000;

      return WyUtils.toNiceTimeFormat(seconds);
    },

    pageChanged(pageNo) {
      this.fetchData(this.searchPath, pageNo);
    },

    async removeSavedTranscript(e, transcript) {
      e.stopPropagation();

      let result = await this.removeTranscript({
        conversationId: transcript.conversationId,
      });
      if (result && result.status && result.status == 200) {
        this.selectedMessage.isSavedTranscript = false;
        this.$forceUpdate();
      }
      // Tooltip display/hide
      if (this.$refs.saveTooltip && this.$refs.unSaveTooltip) {
        this.$refs.unSaveTooltip.$el.style.display = "none";
        this.$refs.saveTooltip.$el.style.display = "block";
      }
    },
    async addSavedTranscript(e, transcript) {
      e.stopPropagation();

      let result = await this.addTranscript({
        conversationId: transcript.conversationId,
      });
      if (result && result.status && result.status == 200) {
        this.selectedMessage.isSavedTranscript = true;
        this.$forceUpdate();
      }
      // Tooltip display/hide
      if (this.$refs.saveTooltip && this.$refs.unSaveTooltip) {
        this.$refs.saveTooltip.$el.style.display = "none";
        this.$refs.unSaveTooltip.$el.style.display = "block";
      }
    },

    async getTranscriptById(item) {
      this.transcriptLoading = true;

      return new Promise(async (resolve) => {
        this.messagesTranscripts = [];
        let res = await this.fetchFlowsTopicConversations({ from: 'flowMessages', messageId: item.messageId });
        if (res && res.data && res.data.length > 0 && res.data[0].messages && res.data[0].messages.length > 0) {
          this.messagesTranscripts = res.data[0].messages;
          this.selectedMessage = item;
          this.selectedMessage.messages = res.data[0].messages;
          this.selectedMessage.conversationId = res.data[0].conversationId;
          this.selectedMessage.sentiment = res.data[0].sentiment;
          this.selectedMessage.agentId = res.data[0].agentId;
          this.selectedMessage.conversationType = res.data[0].conversationType;
          this.selectedMessage.custType = res.data[0].custType;
          this.selectedMessage.agentQueue = res.data[0].agentQueue;
          this.selectedMessage.agentGroup = res.data[0].agentGroup;
          this.selectedMessage.handleTime = res.data[0].handleTime;
        }
        else {
          this.messagesTranscripts = [];
        }
        resolve();
        this.transcriptLoading = false;
      })
    },

    async fetchData(searchPath, pageNo = 1) {
      this.messagesLoading = true;
      this.transcriptLoading = true
      this.messages = null;
      this.messagesTranscripts = null;
      this.searchPath = searchPath;
      this.pageNo = pageNo;

      let result = await this.fetchFlowsMessages({ searchPath: searchPath, pageNo: pageNo, pageSize: this.pageSize + 1 });
      if (result.data && result.data.length > 0) {
        if (result.data.length > this.pageSize) {
          this.messages = result.data.slice(0, result.data.length - 1);
          this.pageItemStart = (this.pageNo - 1) * this.pageSize + 1;
          this.pageItemEnd = this.pageItemStart + this.pageSize - 1;
          this.pageCount = this.pageNo + 1;
        }
        else {
          this.messages = result.data;
          this.pageItemStart = (this.pageNo - 1) * this.pageSize + 1;
          this.pageItemEnd = this.pageItemStart + result.data.length - 1;
        }

        if (this.messages && this.messages.length > 0) {
          await this.getTranscriptById(this.messages[0]);
        }

        this.hasData = true;
      }
      else {
        this.messages = [];
        this.messagesTranscripts = [];
      }
      this.messagesLoading = false;
      this.transcriptLoading = false;

      return new Promise(async (resolve) => {
        resolve(this.messages);
      });
    },

    async loadMessages(data, searchPath) {
      this.messages = null;
      this.messagesTranscripts = null;
      this.searchPath = searchPath;
      this.pageNo = 1;

      if (data && data.length > 0) {
        if (data.length > this.pageSize) {
          this.messages = data.slice(0, data.length - 1);
          this.pageItemStart = (this.pageNo - 1) * this.pageSize + 1;
          this.pageItemEnd = this.pageItemStart + this.pageSize - 1;
          this.pageCount = this.pageNo + 1;
        }
        else {
          this.messages = data;
          this.pageItemStart = (this.pageNo - 1) * this.pageSize + 1;
          this.pageItemEnd = this.pageItemStart + data.length - 1;
        }

        if (this.messages && this.messages.length > 0) {
          await this.getTranscriptById(this.messages[0]);
        }

        this.hasData = true;
      }
      else {
        this.messages = [];
        this.messagesTranscripts = [];
      }
    },

    triggerAmplitude(show = false, conversationId = 0, retrieved_from = "") {
      const eventName = show ? 'transcript_summary_show' : 'transcript_summary_hide';
      this.$router.logAmplitudeEvent({ eventName: eventName, filterProperties: {
        conversationId: conversationId,
        ...(show && { retrieved_from: retrieved_from }),
        transcript_is_expanded: false
      }});
    },

    async handleShowSummary(transcript) {
      this.showTranscriptSummary = true;
      this.transcriptSummaryError = false;
      this.transcriptSummaryLoading = true;

      // if exists in session storage, return data from session storage
      const sessionData = sessionStorage.getItem(transcript.conversationId);
      if (sessionData || transcript.contentSummary) {
        const summary = sessionData ? JSON.parse(sessionData).summary : transcript.contentSummary;
        this.transcriptSummaryLoading = false;
        transcript.contentSummary = '<ul>' + summary.replace(/-/g, "").split("\n").map(function (item) {
          return '<li>' + item.trim() + '</li>';
        }).join("") + '</ul>';
        return this.triggerAmplitude(true, transcript.conversationId, 'cache - session storage')
      }

      // else fetch from network
      const contentSummary = await this.fetchTranscriptSummary({
        conversationId: transcript.conversationId
      })
      this.transcriptSummaryLoading = false;

      if (contentSummary.status == 200 && (contentSummary.data && contentSummary.data.summary)) {
        transcript.contentSummary = '<ul>' + contentSummary.data.summary.replace(/-/g, "").split("\n").map(function (item) {
          return '<li>' + item.trim() + '</li>';
        }).join("") + '</ul>';
        sessionStorage.setItem(transcript.conversationId, JSON.stringify(contentSummary.data));
        this.triggerAmplitude(true, transcript.conversationId, contentSummary.data.retrieved_from)
        return;
      } 
      this.transcriptSummaryError = true;
    },

    handleHideSummary(transcript) {
      if(!this.transcriptSummaryError) {
        this.triggerAmplitude(false, transcript.conversationId)
      }
      this.showTranscriptSummary = false;
      this.transcriptSummaryError = false;
    },
  },
});
</script>

<style lang="scss">
.contact-reason-group-topics-tab-info-pop-over {
  color: #E4E8EC;
  border: black !important;
  line-height: 18px;

  .popper-item {
    margin-bottom: 15px;
  }

  .popper__arrow:after {
    display: block !important;
    border-bottom-color: #000 !important;
  }
}

.wy-flow-messages.hasData {
  display: block;
}

.date {
  font-size: 14px;
  font-weight: 400;
}

.time-range {
  font-size: 14px;
  font-weight: 400;
}


.sentiment-icon-positive {
  cursor: pointer;
  background-image: url('../../assets/icons/icon_neutral_senti.svg');
  background-size: 80%;
  background-repeat: no-repeat;
  background-position-y: 3px;
  background-position-x: 0px;
  position: relative;
  right: 67px;
  bottom: 8.5px;
  border-radius: 100px;
  border: 0px;
  margin: 6px 0px 0 0;
  padding: 12px 10px 14px 14px;
  float: right;
}

.sentiment-icon-neutral {
  cursor: pointer;
  background-image: url('../../assets/icons/icon_neutral_senti.svg');
  background-size: 80%;
  background-repeat: no-repeat;
  background-position-y: 3px;
  background-position-x: 0px;
  position: relative;
  right: 67px;
  bottom: 8.5px;
  border-radius: 100px;
  border: 0px;
  margin: 6px 0px 0 0;
  padding: 12px 10px 14px 14px;
  float: right;
}

.sentiment-icon-negative {
  cursor: pointer;
  background-image: url('../../assets/icons/icon_neutral_senti.svg');
  background-size: 80%;
  background-repeat: no-repeat;
  background-position-y: 3px;
  background-position-x: 0px;
  position: relative;
  right: 67px;
  bottom: 8.5px;
  border-radius: 100px;
  border: 0px;
  margin: 6px 0px 0 0;
  padding: 12px 10px 14px 14px;
  float: right;
}



.contact-reason-group-topics-tab-info-pop-over {
  background: black;
  color: #FFFFFF;
  font-size: 14px;
  font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
}

.wy-flow-messages {
  //display: none;
  height: 100%;

  .transcripts-viewer-header-right {
    overflow-y: auto;
    height: 204px !important;
    padding-right: 20px;

    &::-webkit-scrollbar {
      background-color: #000 !important;
      width: 8px !important;
    }

    &::-webkit-scrollbar-thumb {
      background: #C8C8C8 !important;
      border-radius: 5px;
    }
  }

  .contact-reason-transcript-tab-info-pop-over {
    padding: 12px 7px 10px 15px !important;
  }

  .div-no-content {
    position: relative;
    text-align: center;
    top: 50px;
  }

  .el-loading-mask .el-loading-spinner {
    top: 35%;
    background: var(--site-loading-icon);
    background-repeat: no-repeat;
    background-size: 25px;
    background-position: center;

    .circular {
      display: inline-block;
      width: 90px;
      height: 90px;
    }

    .path {
      animation: loading-dash 2s ease-in-out infinite;
      stroke-dasharray: 90, 150;
      stroke-dashoffset: 0;
      stroke-width: 2.5 !important;
      stroke: #0C2837 !important;
      stroke-linecap: round;
    }
  }

  .topic-group-section {
    width: 100%;
    height: 50%;
    box-sizing: border-box;
    padding: 12px 0px 14px 0px;
    background: #FFFFFF 0% 0% no-repeat padding-box;
    border: 1px solid #E8E8E8;
    border-radius: 10px;
    position: relative;
    bottom: 36px;
    margin-bottom: 0px;
    //border-bottom: none;

    .div-group-viewer-header.div-group-name {
      font-size: 16px;
      font-weight: 500;
      padding: 0px 10px 12px 15px;
      border-bottom: 1px solid #E8E8E8;
    }

    .div-group-viewer-content {
      //margin-top: 10px;
      height: calc(100% - 54px);
      overflow-y: auto;
      overflow-x: hidden;
      margin-bottom: 8px;
      border-bottom: 1px solid #E8E8E8;
    }

    .div-group-topics {
      margin-top: 0px;

      .div-topic-content {
        .div-item {
          color: var(--site-font-primary-color);
          font-weight: normal;
          margin: 0px 0px 0px 0px;
          line-height: 19px;
          padding: 11px 15px 4px 30px;
          font-size: 12px;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          cursor: pointer;

          .div-ellipsis-item {
            width: 88%;
            display: inline-block;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }

        .div-item:hover {
          background: #f5f7fa;
        }

        .div-item.selected {
          background: #E2ECF7;
        }
      }
    }

    .div-group-viewer-footer.div-group-name {
      font-size: 16px;
      font-weight: 500;
      padding: 0px 0px 0px 0px;
    }

    .div-message-transcripts-viewer-footer {
      display: inline-block;
      width: 100%;
      display: flex;
      justify-content: space-around;

      .div-transcript-page-infor {
        float: left;
        padding-top: 2px;
        padding-left: 14px;
        font-size: 12px;
        color: #ADADAD;
      }

      .div-transcript-pagination {
        float: right;
        padding-right: 14px;
        font-size: 12px;
        color: #ADADAD;

        .el-pagination {
          padding-right: 0px;
          // margin-left: 430px;
        }

        .el-pagination button {
          color: #106FBC;
          font-weight: bold;
          padding-right: 0px;
          padding-left: 30px;
          font-size: 12px;
          line-height: unset !important;
          height: unset !important;

          >span {
            line-height: unset !important;
            height: unset !important;
          }
        }

        .el-pagination button:disabled {
          color: #C0C4CC;
        }

        .el-pagination span:not([class*=suffix]) {
          word-spacing: 7px;
        }
      }
    }
  }

  .message-transcripts-section {
    width: 100%;
    height: calc(100% - 50% - 14px);
    box-sizing: border-box;
    padding: 12px 0px 14px 0px;
    background: #FFFFFF 0% 0% no-repeat padding-box;
    border: 1px solid #E8E8E8;
    border-radius: 10px;
    opacity: 1;
    margin-top: 15px;
    position: relative;
    bottom: 36px;

    .div-message-transcripts-viewer-header {
      font-size: 16px;
      font-weight: 500;
      padding: 0px 10px 0px 15px;
      border-bottom: 1px solid #E8E8E8;
      width: 100%;
      box-sizing: border-box;
      min-height: 32px;
      // display: inline-block;
      display: flex;
      justify-content: space-between;

      .header-content {
        display: flex;
      }

      .info-mark {
        float: right;

        .el-infor-icon {
          cursor: pointer;
          background-image: url('../../assets/icons/icon_transcript_search_info.svg');
          background-size: 80%;
          background-repeat: no-repeat;
          background-position-y: 2px;
          background-position-x: 2px;
          height: 28px;
          border-radius: 100px;
          border: 0px;
          margin: 0px 0px 0 0;
          padding: 12px 10px 14px 14px;
          float: right;
          left: 0px;
          top: 4px;
        }

        .el-button:hover,
        .el-button:visited,
        .el-button:focus {
          background-color: unset !important;
          color: none !important;
        }
      }
    }

    .div-message-transcripts-viewer-content {
      //margin-top: 1px;
      height: calc(100% - 43px);
      overflow-y: auto;
      overflow-x: hidden;
      margin-bottom: 30px;
      width: 100%;

      .div-message-transcript-name.cus-style {
        background-color: #F8F6F6;
        margin: 10px 10px;
        border-bottom: unset !important;
      }

      .div-message-transcript-name.bot-style {
        background-color: #EFFCFF;
        margin: 10px 10px;
        border-bottom: unset !important;
      }

      .div-message-transcript-name.agent-style {
        background-color: #EEEEFA;
        margin: 10px 10px;
        border-bottom: unset !important;
      }

      .div-message-transcript-name {
        display: flex;
        padding: 10px 10px 9px 30px;
        font-size: 12px;
        line-height: 16px;
        letter-spacing: 0px;
        cursor: default;

        .div-sent-by {
          font-weight: 500;
          font-size: 14.22px;
          padding-right: 10px;
          color: #000;
          padding-right: 5px;
          min-width: 45px;
          line-height: 19px;

          span.time-stamp-style {
            color: #4B5158;
            font-size: 14.22px;
            font-weight: 400;
            font-weight: 400;
          }
        }

        .div-transcript-text {
          word-break: break-word;
          padding-right: 11px;
          padding-top: 1px;
          font-size: 14.22px;
          color: var(--site-font-secondary-color);
          min-width: 100px !important;
        }
      }

      // .div-message-transcript-name:hover {
      //   background-color: #f5f7fa;
      // }
      .div-message-transcript-name-highlighted {
        background-color: #E2ECF7;
      }

      .transcriptSummary {
        margin-left: -1.7em;
      }
    }
  }

    .summary-icon {
    background-image: url('../../assets/icons/summary_icon.svg');
    background-size: 80%;
    background-repeat: no-repeat;
    background-position-y: 5px;
    background-position-x: 3px;
    height: 30px;
    position: relative;
    left: 7px;
    border: 0px;
    top: 3px !important;
    margin: -1px 13px 0 0;
    padding: 12px 10px 18px 14px;
    float: left;
  }

  .saved-transcript-golden-star {
    background-image: url("../../assets/icons/icon_golden_star.svg");
    margin: 0px;
    margin-top: 3px !important;
    border: 0;
    height: 25px;
    width: 20px;
    background-repeat: no-repeat;
    padding: 0px;
  }

  .saved-transcript-grey-star {
    background-image: url("../../assets/icons/icon_grey_star.svg");
    margin: 0px;
    margin-top: 3px !important;
    border: 0;
    height: 20px;
    width: 20px;
    background-repeat: no-repeat;
    padding: 0px;
  }
}
.wy-flow-conteiner .div-message-transcripts-viewer-content .transcriptSummaryError .content .info {
  .tryAgainBtnFlow {
    border-radius: 4px;
    max-width: 100px;
    max-height: 35px;
    font-size: 16px;
    color: #202223;
    font-weight: 400;
    border: 1px solid #EBAFBA;
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;

    &:hover {
      border: 1px solid #EBAFBA;
      color: #202223;
      background-color: #F4F6F8;
    }
  }
  .el-button {
    padding: 7px 15px 10px 10px;
  }
}

</style>